import React from "react";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { matchPath, useLocation } from "react-router-dom";

import { BASE_FRONTEND_URL } from "@/constants";
import { entityKindMap } from "@/components/FpPages/RMS/Dashboard/utils";

// Exact case throughout: react-router matches case-insensitively by default,
// which would let `/rms/View/managers` through as a record of kind "View".
const exact = (path: string) => ({ path, caseSensitive: true, end: true });

const RECORD_PATTERNS = [
  exact("/rms/:entityKind/:entityId"),
  exact("/rms/:entityKind/:entityId/:panelKind"),
  exact("/rms/:entityKind/:entityId/:panelKind/:subParam"),
];

/** A record kind that has a record page. An interaction is a kind, but its
 *  page is `/rms/interaction/:id`, admitted on its own below. */
function isRecordPageKind(kind: string | undefined): boolean {
  return (
    kind !== undefined &&
    kind !== "interaction" &&
    Object.prototype.hasOwnProperty.call(entityKindMap, kind)
  );
}

/**
 * Whether an address may render inside the frame (#15113). The frame stays on
 * the record (Hunter, 2026-08-11: "Ideally the iframe experience never escapes
 * the /rms route"): a record page and its tabs, an interaction page, and the
 * interaction create page. An allowlist, so a new `/rms/<word>/...` route stays
 * out of the frame until someone admits it here on purpose. The embed's sign-in
 * pages are routed above the frame and never reach here.
 */
export function isAllowedInFrame(pathname: string): boolean {
  if (matchPath(exact("/rms/interactions/create"), pathname)) return true;
  if (matchPath(exact("/rms/interaction/:interactionId"), pathname))
    return true;
  return RECORD_PATTERNS.some((pattern) =>
    isRecordPageKind(matchPath(pattern, pathname)?.params.entityKind)
  );
}

/** How far the "Open in Finpilot" overlay sits from the frame's top edge. */
export const OPEN_IN_FINPILOT_TOP_PX = 12;

/**
 * The top band a framed page leaves clear for the "Open in Finpilot" overlay
 * below: its top offset, the link's height and a small gap (the stories pin
 * that the overlay clears the page). Every framed page whose RMS header stands
 * down reserves it (the record page and the interaction page, #15113), so the
 * overlay never covers content at any frame width: a centred header slides
 * under the frame's top-right corner once the frame is narrow enough.
 */
export const OPEN_IN_FINPILOT_BAND_PX = OPEN_IN_FINPILOT_TOP_PX + 40;

/** The band itself, rendered where the RMS header would be. */
export const OpenInFinpilotBand: React.FC = () => (
  <div
    aria-hidden="true"
    data-open-in-finpilot-band=""
    className="flex-shrink-0"
    style={{ height: OPEN_IN_FINPILOT_BAND_PX }}
  />
);

/**
 * Chrome for the app when it is running embedded in another product's page
 * (#11021).
 *
 * Wraps the whole authenticated tree rather than one page, because embed mode
 * is a property of the document: a viewer who navigates inside the frame is
 * still embedded, and should still get this affordance.
 *
 * It adds a way out — the same view, opened top-level in the real app.
 * Deliberately built from the *live* location so it follows the viewer, and
 * deliberately without the embed parameter so the new tab is the full product
 * rather than another chromeless panel.
 *
 * It also keeps the frame on the record (#15113): any address outside
 * `isAllowedInFrame` renders a panel with that way out instead of the page, so
 * a link nobody has found yet cannot carry the viewer off the record.
 */
const EmbedFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  params.delete("embed");
  const query = params.toString();
  const fullAppUrl = `${BASE_FRONTEND_URL}${location.pathname}${
    query ? `?${query}` : ""
  }`;

  if (!isAllowedInFrame(location.pathname)) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <h1 className="text-base font-medium text-[#10141B]">
            This page opens in Finpilot
          </h1>
          <a
            href={fullAppUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-700"
          >
            Open in Finpilot
            <ArrowSquareOutIcon size={14} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      {children}
      <a
        href={fullAppUrl}
        target="_blank"
        rel="noreferrer"
        style={{ top: OPEN_IN_FINPILOT_TOP_PX }}
        className="absolute right-4 z-50 inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-[#10141B] shadow-sm hover:bg-gray-50"
      >
        Open in Finpilot
        <ArrowSquareOutIcon size={14} />
      </a>
    </div>
  );
};

export default EmbedFrame;
